import { ref } from 'vue';
import api from '@/utils/axios';

function getToken() {
  return localStorage.getItem('srToken') || '';
}

export function useDataImport() {
  const uploading = ref(false);
  const promoting = ref(false);
  const loadingStatus = ref(false);
  const error = ref('');
  const run = ref(null);
  const report = ref(null);
  const canPromote = ref(false);

  const applyRunPayload = (data) => {
    if (!data) {
      run.value = null;
      report.value = null;
      canPromote.value = false;
      return;
    }
    run.value = data.run || data;
    const r = run.value?.report || data.report || null;
    report.value = r;
    canPromote.value = Boolean(data.canPromote ?? r?.canPromote);
  };

  const fetchStatus = async (runId = '') => {
    loadingStatus.value = true;
    error.value = '';
    try {
      const res = await api.post('/import/status', {
        token: getToken(),
        runId: runId || undefined
      });
      const data = res.data?.data;
      applyRunPayload(data?.run ? { run: data.run, canPromote: data.run.canPromote } : null);
      return data;
    } catch (e) {
      error.value = e.response?.data?.error?.message || e.message || 'Failed to load import status';
      throw e;
    } finally {
      loadingStatus.value = false;
    }
  };

  const uploadFile = async (file) => {
    if (!file) return null;
    uploading.value = true;
    error.value = '';
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('token', getToken());
      const res = await api.post('/import/upload', form, {
        headers: { 'Content-Type': undefined }
      });
      const data = res.data?.data;
      applyRunPayload(data);
      return data;
    } catch (e) {
      error.value = e.response?.data?.error?.message || e.message || 'Upload failed';
      throw e;
    } finally {
      uploading.value = false;
    }
  };

  const validateRun = async (runId) => {
    if (!runId) return null;
    loadingStatus.value = true;
    error.value = '';
    try {
      const res = await api.post('/import/validate', {
        token: getToken(),
        runId
      });
      const data = res.data?.data;
      applyRunPayload(data);
      return data;
    } catch (e) {
      error.value = e.response?.data?.error?.message || e.message || 'Validation failed';
      throw e;
    } finally {
      loadingStatus.value = false;
    }
  };

  const promoteRun = async (runId, mode = 'merge') => {
    if (!runId) return null;
    promoting.value = true;
    error.value = '';
    try {
      const res = await api.post('/import/promote', {
        token: getToken(),
        runId,
        confirm: true,
        mode
      });
      await fetchStatus(runId);
      return res.data?.data;
    } catch (e) {
      error.value = e.response?.data?.error?.message || e.message || 'Promote failed';
      throw e;
    } finally {
      promoting.value = false;
    }
  };

  return {
    uploading,
    promoting,
    loadingStatus,
    error,
    run,
    report,
    canPromote,
    fetchStatus,
    uploadFile,
    validateRun,
    promoteRun
  };
}
